import { isProductionHost } from "consts";
import { useFlags } from "hooks/useFlags";
import { useSearchParam } from "hooks/useSearchParam";
import { useUser } from "hooks/useUser";
import { useMemo } from "react";
import type { TabBarOption } from "ui/TabBar";

export type ProfileTab =
  "moderatorial" | "editorial" | "export" | "scripts" | "profile";

type UseProfileTabs = {
  options: TabBarOption<ProfileTab>[];
  activeTab: ProfileTab;
  setTab: (tab: ProfileTab) => void;
};

// Порядок вкладок слева направо совпадает с приоритетом вкладки по умолчанию:
// модераторская → редакторская → личная.
export const useProfileTabs = (): UseProfileTabs => {
  const { isModerator, isContentEditor, isAdmin } = useUser();
  const { flags } = useFlags();

  const [rawTab, setRawTab] = useSearchParam("tab");

  const options = useMemo<TabBarOption<ProfileTab>[]>(() => {
    const result: TabBarOption<ProfileTab>[] = [];

    // Ролевые вкладки видны только при наличии права И включённом флаге —
    // иначе вкладка показывала бы тело с текстом «функция выключена».
    if (isModerator && flags.moderationEnabled)
      result.push({ value: "moderatorial", label: "Модераторское" });

    if (isContentEditor && flags.editorialTest)
      result.push({ value: "editorial", label: "Редакторское" });

    // Выгрузка данных — только по праву модерации; флаг не нужен: эндпоинт от него не зависит.
    if (isModerator) result.push({ value: "export", label: "Экспорт" });

    // Скрипты наполнения БД — только для админов и никогда на проде (бэкенд там тоже отдаёт 404).
    if (isAdmin && !isProductionHost)
      result.push({ value: "scripts", label: "Скрипты" });

    // Личная вкладка (форма профиля) всегда доступна и стоит справа.
    result.push({ value: "profile", label: "Личное" });

    return result;
  }, [
    isModerator,
    isContentEditor,
    isAdmin,
    flags.moderationEnabled,
    flags.editorialTest,
  ]);

  // Вкладка по умолчанию — первая из видимых (модераторская, иначе редакторская,
  // иначе личная). «Экспорт» и «Скрипты» по умолчанию не открываем — служебные вкладки.
  const defaultTab = (
    options.find(
      (option) => option.value !== "export" && option.value !== "scripts",
    ) ?? options[0]
  ).value;

  // Учитываем ?tab= только если он указывает на реально видимую вкладку,
  // иначе откатываемся к дефолтной (устаревшая ссылка не ломает страницу).
  const activeTab =
    options.find((option) => option.value === rawTab)?.value ?? defaultTab;

  const setTab = (tab: ProfileTab) => setRawTab(tab);

  return { options, activeTab, setTab };
};
